// api.js
// Use environment variable for the API endpoint, with a fallback for local development
const API_URL = process.env.REACT_APP_API_URL || 'https://prompt-engineer-worker.zlceapps.workers.dev';

// AI model to use for generating prompts
const MODEL = 'gpt-4o-mini';

// Function to generate initial questions based on project description
export async function generateQuestions(description, parentQuestionId = null) {
  const systemPrompt =
`# Role
You are an expert software developer who specializes in turning app ideas into working code quickly. Your mission is to ask targeted questions that extract exactly what's needed to build a functional MVP immediately.

# Task
Follow these steps in order:

1. First, carefully analyze the user's project description to understand their core concept.
2. Then, generate 10-15 focused questions that will extract ALL information needed to start coding immediately.
3. For each question, provide 4-6 practical answer options, prioritizing simpler approaches that can be implemented quickly.
4. Ensure ZERO OVERLAP between questions - each must address a completely distinct aspect of the project.
5. Format your response as a JSON array following the exact structure shown at the end of these instructions.

# Essential Question Categories (include at least one from each)

## PROJECT SCOPE
- Is this a solo project or team effort?
- What's the available time commitment? (hours, days, weeks)
- Is this for learning, hobby, professional use, or potential business?
- What's the developer's experience level with relevant technologies?

## CORE FUNCTIONALITY
- What are the absolute minimum features needed for a working MVP?
- What defines "success" for the day-one version?
- Which user flows must be implemented for the MVP to be useful?
- What data must be persisted between sessions?

## TECHNICAL FOUNDATION
- Which frontend framework/approach is preferred?
- What backend technology is appropriate (if needed)?
- What database requirements exist (if any)?
- How will the app be deployed/hosted?

## IMPLEMENTATION CONSTRAINTS
- What features should be deferred to later versions?
- What performance requirements exist for the MVP?
- Are there any specific compatibility requirements?
- What simplifications are acceptable for the first version?

# For Games/Interactive Experiences
- Identify only the core mechanics needed for a minimal playable version
- Focus on the simplest game state representation that would work
- Ask about essential player interactions and controls
- Determine the basic feedback systems needed for playability

# Response Format
Return your response EXACTLY as a JSON array with this structure:
"""
[
  {
    "question": "What is your time availability for this project?",
    "options": ["Few hours (need working code today)", "Weekend project (1-2 days)", "Part-time over a few weeks", "Full-time dedicated development", "Ongoing hobby project", "Other (explain in notes)"],
    "triggersFollowUp": true
  }
]
"""

The "triggersFollowUp" property should be true for questions where implementation details would significantly impact the coding approach.
`;

  let userPrompt;
  
  if (description.requestType === "additional") {
    // This is a request for additional user experience questions
    userPrompt = `
# Additional Questions Task - INSPIRE & CREATE WITHOUT DUPLICATION

## Input Information
Project description: """${description.description}"""
Previous answers and questions: """${JSON.stringify(description.currentAnswers, null, 2)}"""

## TWO CRITICAL REQUIREMENTS
1. AVOID ANY duplication with previous questions (even if phrased differently)
2. Focus on INSPIRING, CREATIVE aspects that will excite the user about their app

## Anti-Duplication Process
1. Review ALL previous questions thoroughly
2. Create a mental list of topics already covered
3. Identify completely fresh angles and perspectives
4. Only ask questions about aspects not yet discussed

## Focus on INSPIRING Categories
Choose from these exciting, forward-thinking categories that spark creativity:

### USER DELIGHT FACTORS
- What unique interactions could make users smile?
- How might the app surprise and delight first-time users?
- What small touches could create memorable moments?
- Which subtle animations or effects would enhance the experience?

### FEATURE EXPANSION IDEAS
- What creative features could set this app apart from competitors?
- How might the core concept evolve in interesting ways?
- What complementary capabilities would enhance the main functionality?
- Which unexpected twists could make standard features more engaging?

### EXPERIENCE ENHANCEMENT
- How could the app create moments of joy or increased engagement?
- What visual themes or styles would perfectly match the app's purpose?
- How might sound, haptics, or animation create a more immersive experience?
- What personalization options would make users feel connected to the app?

### COMMUNITY & SOCIAL ASPECTS
- How might users share their experiences with others?
- What community features could enhance the core functionality?
- How could user-generated content improve the experience?
- What social interactions would make sense for this concept?

## Question Guidelines
- Keep questions SIMPLE and ACCESSIBLE - assume the audience has a basic technical knowledge like a product manager level of knowledge
- Focus on EXCITING possibilities that inspire creativity
- Ask about features and experiences
- Continue to build on the knowledge gained from previous questions about technical aspects
- Present options that spark imagination and enthusiasm

- Ensure questions are completely different from previous ones
- Always include "Other creative idea (explain in notes)" as a final option

## Response Format
Return your response EXACTLY as a JSON array with this structure:
"""
[
  {
    "question": "What unique feature could make users smile when they first use the app?",
    "options": ["A playful welcome animation", "Subtle easter eggs to discover", "Personalized greeting based on time of day", "Confetti celebration for completing setup", "Unexpected but helpful tips", "Other creative idea (explain in notes)"],
    "triggersFollowUp": true
  }
]
"""

Remember: Your goal is to inspire creativity and excitement about the app concept through completely fresh questions. Avoid technical implementation details and focus on delightful user experiences.`;
  } 
  else if (description.requestType === "additional_tech") {
    // This is a request for additional technical questions
    userPrompt = `
Based on the project description: "${description.description}" and these technology choices already made: ${JSON.stringify(description.currentAnswers, null, 2)}, I need HIGHLY SPECIFIC technical implementation questions that drill deeper into the selected tech stack.
⚠️ CRITICAL REQUIREMENT: DO NOT GENERATE ANY QUESTIONS THAT DUPLICATE EXISTING ONES IN ANY WAY, SHAPE, OR FORM ⚠️
Even if a question seems "related" but "different" - if it covers the same decision point or information, it is a DUPLICATE. Each question must extract COMPLETELY NEW information.
Generate technical questions that explore:

IMPLEMENTATION SPECIFICS:
Specific versions, configurations, and architectures
Build tools, package managers, and deployment pipelines
Data layer implementation details
State management approaches

INTEGRATION POINTS:
API design patterns and standards
Service communication methods
Authentication implementation details
Third-party service integrations

OPTIMIZATION STRATEGIES:
Caching mechanisms and strategies
Performance optimization techniques
Bundle size management
Database query optimization

DEVELOPMENT WORKFLOW:
CI/CD pipeline specifics
Testing frameworks and approaches
Code quality tools and standards
Versioning strategies

Before generating each question:
VERIFY it doesn't overlap with ANY previously asked questions
CONFIRM it extracts new, essential technical information
ENSURE it focuses on implementation rather than conceptual choices

The answers to these questions must provide the specific technical details a developer would need to begin implementing the chosen stack immediately, with zero ambiguity about the implementation approach.
⚠️ AGAIN: ABSOLUTELY NO DUPLICATE QUESTIONS! Each question must extract entirely new information. ⚠️`;
  }
  else if (parentQuestionId) {
    // This is a follow-up request based on previous answers
    userPrompt = `# Follow-up Questions Task

## Input Information
- Project description: """${description.description}"""
- Previous answers: """${JSON.stringify(description.currentAnswers, null, 2)}"""
- Question requiring follow-up: ${parentQuestionId}

## Step-by-Step Instructions
1. Analyze the user's selected answer for question ${parentQuestionId}
2. Identify specific implementation details that need further clarification
3. Generate 3-5 targeted follow-up questions focused on immediate implementation
4. For each question, provide 4-6 practical answer options
5. Format your response as a JSON array following the required structure

## Focus Areas Based on Question Type

### For Technology Choices
- Required libraries/packages and their versions
- Configuration and setup requirements
- Integration points with other selected technologies
- Implementation patterns specific to this technology
- Development environment setup

### For Feature Implementation
- Data models and relationships needed
- User interaction flows and their implementation
- API endpoints or function interfaces
- Error handling and edge cases
- Performance considerations

### For UI/UX Elements
- Component structure and organization
- State management approach
- User feedback mechanisms
- Responsive design considerations
- Accessibility implementation

### For Games/Interactive Experiences
- Core mechanics implementation approach
- Game state management strategy
- Player interaction handling
- Feedback systems implementation
- Essential game loop elements

## Response Requirements
- Questions must directly relate to implementing the chosen option
- Focus ONLY on details needed for IMMEDIATE implementation
- Each question should have practical, implementable options
- Include "Other (explain in notes)" as the final option
- Avoid any overlap with previous questions

## Response Format
Return your response EXACTLY as a JSON array with this structure:
"""
[
  {
    "question": "Which specific libraries/packages are needed to implement this feature?",
    "options": ["Option A with specific details", "Option B with specific details", "Option C with specific details", "Minimal dependencies approach", "Comprehensive toolkit approach", "Other (explain in notes)"],
    "triggersFollowUp": false
  }
]
"""

Remember: Your goal is to extract the specific implementation details needed to start coding this feature TODAY.`;
  } else {
    // This is the initial questions request
    userPrompt = `# Initial Questions Task

## Project Description
"""${description}"""

## Your Task
Generate 10-15 practical questions that will extract exactly what's needed to transform this concept into working code TODAY. Think like a developer who needs to build a functional MVP immediately.

## Step-by-Step Process
1. Carefully analyze the project description to understand the core concept
2. Identify key information needed for immediate implementation
3. Create focused questions covering all essential aspects of the project
4. For each question, provide 4-6 practical answer options
5. Format your response as a JSON array following the required structure

## Essential Question Categories (include at least one from each)

### PROJECT SCOPE & RESOURCES
- Is this a solo project or team effort?
- What's the available time commitment (hours, days, weeks)?
- What's the project purpose (hobby, learning, professional, startup)?
- What's the developer's experience level with relevant technologies?
- Any budget or resource limitations to consider?

### CORE FUNCTIONALITY
- What are the absolute essential features needed for a working MVP?
- What defines "success" for the initial version?
- Which user flows must be implemented for the MVP to be useful?
- What data must be persisted between sessions?
- What are the critical user interactions?

### TECHNICAL FOUNDATION
- Which frontend framework/approach is preferred?
- What backend technology is appropriate (if needed)?
- What database requirements exist (if any)?
- What authentication needs exist (if required)?
- How will the app be deployed/hosted?

### IMPLEMENTATION BOUNDARIES
- What features should be deferred to later versions?
- What simplifications are acceptable for the first version?
- What visual design expectations exist for the MVP?
- What performance requirements exist for initial release?
- What are acceptable limitations for day-one functionality?

### FOR GAMES/INTERACTIVE EXPERIENCES
- What core gameplay mechanics are needed for a playable version?
- What minimum content is required for the initial version?
- What essential player interactions and controls are needed?
- What basic progression or scoring systems are required?
- What feedback mechanisms are essential for gameplay?

## Question Guidelines
- Focus on information needed for IMMEDIATE implementation
- Prioritize practical, actionable details over theoretical concepts
- For technical questions, emphasize approaches that can be implemented quickly
- For feature questions, focus on what's essential vs. what can be added later
- Include options with varying complexity, clearly indicating implementation effort
- Always include "Other (explain in notes)" as the final option

## Response Format
Return your response EXACTLY as a JSON array with this structure:
"""
[
  {
    "question": "What is your time availability for this project?",
    "options": ["Few hours (need working code today)", "Weekend project (1-2 days)", "Part-time over a few weeks", "Full-time dedicated development", "Ongoing hobby project", "Other (explain in notes)"],
    "triggersFollowUp": true
  }
]
"""

Remember: Each question must extract information that directly impacts HOW to implement this project as working code TODAY.
    `;
  }

  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: MODEL,
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        temperature: 0.7
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || `API request failed with status ${response.status}`);
    }

    const data = await response.json();
    
    // Parse the JSON response from the content
    const content = data.choices[0].message.content;
    console.log("Raw content from API:", content);
    
    // Check if content is empty or not valid
    if (!content || typeof content !== 'string' || content.trim() === '') {
      console.error("Empty or invalid content received from API");
      throw new Error("Received empty response from API");
    }
    
    // Remove markdown code block markers if present
    function cleanContent(str) {
      let cleaned = str.trim();
      if (cleaned.startsWith("```")) {
        cleaned = cleaned.replace(/^```(\w+)?\s*/, '').replace(/\s*```$/, '');
      }
      console.log("Cleaned content:", cleaned);
      return cleaned;
    }
    
    try {
      const cleanedContent = cleanContent(content);
      return JSON.parse(cleanedContent);
    } catch (parseError) {
      console.error("JSON Parse Error:", parseError);
      console.error("Content that failed to parse:", content);
      
      // If JSON parsing fails, attempt to create a basic structure
      // This is a fallback to prevent the app from completely failing
      return [
        {
          question: "Could not generate questions. Please try again.",
          options: ["Try again", "Modify your description"],
          triggersFollowUp: false
        }
      ];
    }
  } catch (error) {
    console.error('Error calling API via worker:', error);
    throw error;
  }
}

// Function to generate the final prompt based on all answers
export async function generatePrompt(description, answers) {
  const systemPrompt = `# Role and Purpose
You are an expert AI prompt engineer who specializes in creating comprehensive specifications for app development. Your task is to craft a detailed prompt that a user can copy and paste into ANY AI assistant to generate a high-quality implementation of their app concept, with consideration for both immediate needs AND future expansion.

# Step-by-Step Instructions
1. Carefully analyze the project description and all question responses
2. Extract all technical specifications, user preferences, and potential future needs
3. Organize this information into a detailed, structured description of what needs to be built
4. Balance immediate MVP requirements WITH considerations for future growth
5. Include both technical specifications AND comprehensive feature descriptions

# Critical Requirements

## Content Balance
- Create a prompt that COMPREHENSIVELY DESCRIBES what the user wants to build
- STRICTLY RESPECT all user technology choices without any substitution
- Detail both the immediate MVP AND possible future enhancements
- Focus on REQUIREMENTS and ARCHITECTURE more than specific implementation details
- Adapt to the user's indicated project scope while suggesting scalable approaches
- Include both functional requirements AND considerations for maintainability/extensibility

## Structure Requirements
- Begin with clear instructions about WHAT the app should accomplish and how it should function
- Provide comprehensive context about the app purpose, users, and goals
- Detail ALL technical requirements while explaining WHY each choice matters
- Balance immediate feature needs with thoughtful architecture for future growth
- Describe user experiences and feature behaviors in detail
- Guide another AI to consider both immediate implementation AND long-term scalability
- End with instructions for well-structured, maintainable, and extensible implementation

## For Games/Interactive Applications
- Detail both core MVP gameplay AND potential future enhancements
- Describe the player experience and progression system with room for expansion
- Balance immediate mechanics with architecture that allows for new content/features
- Include considerations for both initial release AND ongoing development

# Final Output Format
Your output must be a COMPLETE PROMPT that the user can directly copy and paste into any AI assistant. It should provide comprehensive requirements and architecture guidance that will result in high-quality code - not just for today's MVP but with consideration for how the application might grow and evolve.`;

  const userPrompt = `# Input Information
Project description: """${description}"""
User requirements: """${JSON.stringify(answers, null, 2)}"""

# Your Task
Create a COMPREHENSIVE prompt that the user can directly copy and paste into ANY AI assistant to guide it in developing their application. This prompt should focus on DETAILED REQUIREMENTS and ARCHITECTURAL CONSIDERATIONS, balancing immediate MVP needs with future extensibility.

# Step-by-Step Process
1. Analyze the project description and requirements thoroughly
2. Extract ALL technical specifications and preferences
3. Identify both immediate needs AND potential future enhancements
4. Organize into a clear, structured description of what should be built
5. Ensure the prompt describes WHAT to build more than HOW to code it

# Essential Components to Include

## Core Balance
- The prompt must COMPREHENSIVELY DESCRIBE what the user wants to build
- It must respect the user's indicated project scope and constraints
- It must honor ALL specified technology choices WITHOUT alternatives
- It must address BOTH immediate MVP needs AND future expansion possibilities

## The Prompt Structure Should:

### 1. DEFINE THE VISION
- Clearly articulate what the application does and its purpose
- Describe both essential MVP features AND potential future enhancements
- Detail the user experience and critical user journeys
- Explain how the application might grow and evolve over time

### 2. DESCRIBE TECHNICAL ARCHITECTURE
- Outline the overall architecture with consideration for scalability
- Specify the chosen technologies with rationale for each
- Describe data models and relationships with room for expansion
- Detail API structures or interfaces with extensibility in mind
- Include authentication/authorization approach if required

### 3. DETAIL FEATURE REQUIREMENTS
- Thoroughly describe each feature's behavior and purpose
- Prioritize features clearly (MVP vs. future enhancements)
- Explain how features should work together
- Include error handling and edge case considerations
- Describe both functional AND non-functional requirements

### 4. PROVIDE IMPLEMENTATION GUIDANCE
- Suggest project structure that allows for future growth
- Include considerations for maintainability and extendability
- Describe testing approach and quality considerations
- Outline deployment strategy aligned with user's choices

## For Games/Interactive Experiences
- Detail both core gameplay AND potential expansions
- Describe progression systems with room for additional content
- Explain both immediate mechanics AND future enhancement possibilities
- Detail player experience and emotional journey

# Output Format Requirements
1. The prompt MUST be ready to COPY AND PASTE directly into an AI assistant
2. Structure it to provide COMPREHENSIVE REQUIREMENTS, not just code instructions
3. Make it detailed enough to generate well-architected, forward-thinking implementation
4. Balance immediate MVP needs with future-proof architecture
5. Remove any meta-commentary or instructions not meant for the AI

# Starting Format
The prompt MUST begin with:
"You are an expert developer tasked with building [app name/description]. Create a complete implementation of this application using [technologies]. Focus on delivering a well-structured solution that not only meets immediate MVP requirements but is also designed for future enhancement and scalability."`;

  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: MODEL,
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        temperature: 0.7
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || `API request failed with status ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    console.log("Prompt content from API:", content);
    return content;
  } catch (error) {
    console.error('Error calling API via worker:', error);
    throw error;
  }
}

// Helper function to check if the API is accessible
export async function checkApiConnection() {
  try {
    const response = await fetch(`${API_URL}/health`, { 
      method: 'GET'
    });
    
    return response.ok;
  } catch (error) {
    console.error('API connection check failed:', error);
    return false;
  }
}